import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { SPONSA_CSS } from "./sponsaCSS";
import ConfirmModal from "../../../components/Dashcomponents/New/ConfirmModal";
import OnboardingCompleteModal from "../../../components/Dashcomponents/OnboardingCompleteModal.jsx";
import API from "../../../api/api";
import {
  setPayPalID,
  setCurrency,
  setRyftID,
} from "../../../State/authSlicer.js";
import { useUnsavedChanges } from "../../../hooks/useUnsavedChanges.js";
import { setToast } from "../../../State/toastSlice.js";
import apisecured from "../../../api/apisecured.js";

function SaveBar({ visible, onDiscard, onSave }) {
  return (
    <div className={`save-bar${visible ? " visible" : ""}`}>
      <div className="save-bar-inner">
        <span className="save-bar-text">You have unsaved changes</span>
        <button className="btn btn-secondary" onClick={onDiscard}>
          Discard
        </button>
        <button className="btn btn-primary" onClick={onSave}>
          Save Changes
        </button>
      </div>
    </div>
  );
}

export default function PaymentSettingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userlogin = useSelector((state) => state.auth);

  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");
  const message = searchParams.get("message");

  // ===== Currency State =====
  const [dirty, setDirty] = useState(false);
  const [currency, setCurrencyState] = useState(userlogin.currency || "GBP");
  const [savedCurrency, setSavedCurrency] = useState(
    userlogin.currency || "GBP",
  );
  const [showWarn, setShowWarn] = useState(false);

  // ===== Payment Processor States =====
  const [paypalConnected, setPaypalConnected] = useState(false);
  const [isConnectingRyft, setIsConnectingRyft] = useState(false);
  const [isConnectingPaypal, setIsConnectingPaypal] = useState(false);
  const [ryftConnected, setRyftConnected] = useState(false);

  // ===== UI State =====
  const [copied, setCopied] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showDisconnectPaypal, setShowDisconnectPaypal] = useState(false);
  const [showDisconnectRyft, setShowDisconnectRyft] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [uidToDelete, setUidToDelete] = useState(null);
  const [ryftConnectedEmail, setRyftConnectedEmail] = useState(null);

  // ===== Unsaved changes guard (header modal) =====
  useUnsavedChanges(dirty);

  const alertUrl = "https://sponsa.app/alerts/your-token-here";

  // ===== Error toast from URL params =====
  useEffect(() => {
    if (error && message) {
      dispatch(
        setToast({ message: decodeURIComponent(message), type: "error" }),
      );
    }
  }, [error, message]);

  // ===== Show onboarding success modal from URL =====
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("success") === "true") {
      setShowSuccessModal(true);
      params.delete("success");
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("ryft_success") === "true") {
      setRyftConnected(true);
      setShowSuccessModal(true);
      params.delete("ryft_success");
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
    if (params.get("ryft_refresh") === "true") {
      dispatch(
        setToast({
          message: "Ryft session expired. Please try connecting again.",
          type: "error",
        }),
      );
      params.delete("ryft_refresh");
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, []);

  // ===== Check PayPal connection on mount =====
  useEffect(() => {
    const checkPayPalConnection = async () => {
      try {
        const response = await apisecured.get(
          "/paypal/check-paypal-connection",
        );
        const { connected, paypalID, connectedEmail } = response.data;
        setPaypalConnected(connected);
        if (connected) {
          dispatch(
            setPayPalID({ paypalID, paypalConnectedEmail: connectedEmail }),
          );
        }
      } catch (err) {
        console.error("Error checking PayPal connection:", err);
      }
    };

    checkPayPalConnection();
  }, [userlogin.uid, dispatch]);

  useEffect(() => {
    const checkRyftConnection = async () => {
      try {
        const response = await apisecured.get("/ryft/check-ryft-connection");
        const { connected, subAccountID } = response.data;
        setRyftConnected(connected);
        setRyftConnectedEmail(subAccountID);
        if (connected) {
          dispatch(setRyftID({ ryftSubAccountID: subAccountID }));
        }
      } catch (err) {
        console.error("Error checking Ryft connection:", err);
      }
    };
    checkRyftConnection();
  }, [userlogin.uid]);
  // ===== Currency =====
  const mark = () => setDirty(true);

  const handleCurrencyChange = (v) => {
    setCurrencyState(v);
    setShowWarn(v !== savedCurrency);
    mark();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(alertUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSave = async () => {
    try {
      const response = await axios.post(
        `${API}account/update-currency?uid=${userlogin.uid}&currency=${currency}`,
      );
      if (response.status === 200) {
        dispatch(setCurrency(currency));
        setSavedCurrency(currency);
        setDirty(false);
        setShowWarn(false);
        dispatch(
          setToast({
            message: "Currency changed successfully",
            type: "success",
          }),
        );
      } else {
        dispatch(
          setToast({
            message: "An error occurred while changing the currency",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setToast({
          message: "An error occurred while changing the currency",
          type: "error",
        }),
      );
    }
  };

  const handleDiscardClick = () => {
    setShowDiscardModal(true);
  };

  const handleDiscardConfirm = () => {
    setCurrencyState(savedCurrency);
    setShowWarn(false);
    setDirty(false);
    setShowDiscardModal(false);
  };

  // ===== PayPal =====
  const connectPaypal = async () => {
    if (isConnectingPaypal) return;
    setIsConnectingPaypal(true);
    try {
      const { data } = await apisecured.get(
        `account/connect?uid=${userlogin.uid}`,
      );
      window.location.href = data.url;
    } catch (err) {
      dispatch(
        setToast({ message: "Error connecting to PayPal", type: "error" }),
      );
      console.error("Error connecting to PayPal:", err);
    } finally {
      setIsConnectingPaypal(false);
    }
  };

  const openPayPalDeleteModal = () => {
    setShowDisconnectPaypal(true);
    setUidToDelete(userlogin.uid);
  };

  const handleDisconnectPaypal = async () => {
    try {
      await apisecured.post("/paypal/remove-paypal-connection");

      setPaypalConnected(false);
      dispatch(setPayPalID({ paypalID: null, paypalConnectedEmail: null }));
      setShowDisconnectPaypal(false);
      setUidToDelete(null);

      dispatch(
        setToast({
          message: "PayPal disconnected",
          type: "success",
        }),
      );
    } catch (err) {
      const responseData = err?.response?.data;

      const traceId =
        responseData?.traceId || responseData?.extensions?.traceId || null;

      const detail =
        responseData?.detail ||
        responseData?.title ||
        responseData?.message ||
        "Error disconnecting PayPal";

      dispatch(
        setToast({
          message: traceId ? `${detail} Trace ID: ${traceId}` : detail,
          type: "error",
        }),
      );

      console.error("Error disconnecting PayPal:", {
        status: err?.response?.status,
        data: responseData,
        traceId,
        err,
      });
    }
  };

  // ===== Ryft =====
  const handleConnectRyft = async () => {
    if (isConnectingRyft) return;

    setIsConnectingRyft(true);

    try {
      const { data } = await apisecured.get(
        `account/ryft/connect?uid=${userlogin.uid}`,
        {
          headers: {
            Authorization: `Bearer ${userlogin.token}`,
          },
        },
      );

      // Backend says account is already connected
      if (data.connected === true) {
        setRyftConnected(true);
        setRyftConnectedEmail(data.subAccountId);
        dispatch(setRyftID({ ryftSubAccountID: data.subAccountId }));

        dispatch(
          setToast({
            message: data.message || "Ryft is already connected.",
            type: "success",
          }),
        );

        return;
      }

      // Normal Ryft onboarding redirect
      if (data.url) {
        window.location.href = data.url;
        return;
      }

      // Unexpected successful response
      dispatch(
        setToast({
          message: "Ryft connection URL was not returned.",
          type: "error",
        }),
      );

      console.error("Unexpected Ryft connect response:", data);
    } catch (err) {
      const responseData = err?.response?.data;

      const traceId =
        responseData?.traceId || responseData?.extensions?.traceId || null;

      const errorCode =
        responseData?.errorCode || responseData?.extensions?.errorCode || null;

      const detail =
        responseData?.detail ||
        responseData?.title ||
        responseData?.message ||
        "Error connecting to Ryft";

      dispatch(
        setToast({
          message: traceId ? `${detail} Trace ID: ${traceId}` : detail,
          type: "error",
        }),
      );

      console.error("Error connecting to Ryft:", {
        status: err?.response?.status,
        data: responseData,
        traceId,
        errorCode,
        err,
      });
    } finally {
      setIsConnectingRyft(false);
    }
  };

  const handleDisconnectRyft = async () => {
    try {
      await apisecured.delete("/ryft/disconnect");

      setRyftConnected(false);
      setRyftConnectedEmail(null);
      dispatch(setRyftID({ ryftSubAccountID: null }));
      setShowDisconnectRyft(false);
      setUidToDelete(null);

      dispatch(
        setToast({
          message: "Ryft disconnected",
          type: "success",
        }),
      );
    } catch (err) {
      const responseData = err?.response?.data;

      const traceId =
        responseData?.traceId || responseData?.extensions?.traceId || null;

      const detail =
        responseData?.detail ||
        responseData?.title ||
        responseData?.message ||
        "Error disconnecting Ryft";

      dispatch(
        setToast({
          message: traceId ? `${detail} Trace ID: ${traceId}` : detail,
          type: "error",
        }),
      );

      console.error("Error disconnecting Ryft:", {
        status: err?.response?.status,
        data: responseData,
        traceId,
        err,
      });
    }
  };

  // ===== Onboarding modal =====
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    const params = new URLSearchParams(location.search);
    params.delete("success");
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  return (
    <>
      <style>{SPONSA_CSS}</style>
      <style>{`
        .payment-icon.disconnected svg {
          filter: grayscale(1) brightness(0.4);
          transition: filter 0.2s ease;
        }
        .currency-warning {
          background:rgba(251, 191, 36, 0.1);
          color: #fbbf24;
        }
        .currency-warning svg {
          flex-shrink: 0;
          margin-top: 2px;
        }
        .badge-new {
          font-size: 10px;
          font-weight: 600;
          color: #00d4aa;
          background: rgba(0, 212, 170, 0.1);
          padding: 2px 6px;
          border-radius: 4px;
          margin-left: 6px;
          vertical-align: middle;
        }
      `}</style>

      <div className="sp-settings-wrap">
        <div className="sp-settings-inner">
          <header className="page-header">
            <h1 className="page-title">Payment Settings</h1>
            <p className="page-description">
              Manage how you receive tips from your supporters.{" "}
              <a
                href="https://help.sponsa.app/for-creators/4yqnnFziGPBYDwLaFWA41L/payments--fees/6HKTsb2J8bZMGBbetRoYBj"
                target="_blank"
                rel="noopener noreferrer"
              >
                Payments &amp; fees
              </a>
              {" · "}
              <a
                href="https://help.sponsa.app/for-creators/4yqnnFziGPBYDwLaFWA41L/what-information-can-supporters-see-/6HKTsb2J87YmPnxmLmLZsk"
                target="_blank"
                rel="noopener noreferrer"
              >
                What can supporters see?
              </a>
            </p>
          </header>

          {/* ===== Currency Section ===== */}
          <section className="settings-section">
            <div className="section-header">
              <div className="section-icon green">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <line x1="12" y1="2" x2="12" y2="22" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
              <div className="section-title-group">
                <h2 className="section-title">Currency</h2>
                <p className="section-subtitle">
                  Choose the currency supporters pay in
                </p>
              </div>
            </div>
            <div className="form-row">
              <label className="form-label">Tip Currency</label>
              <select
                className="form-select"
                value={currency}
                onChange={(e) => handleCurrencyChange(e.target.value)}
              >
                <option value="GBP">🇬🇧 GBP — British Pound (£)</option>
                <option value="USD">🇺🇸 USD — US Dollar ($)</option>
              </select>
              <p className="form-help">
                This is the currency displayed on your page and used for
                payments.
              </p>
              {showWarn && (
                <div className="currency-warning flex items-start gap-[10px] mt-[12px] px-[14px] py-[12px] rounded-[10px] text-[13px] leading-normal animate-[fadeIn_0.2s_ease]">
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span>
                    Changing your currency will update how amounts are displayed
                    on your page and goals. Existing goal targets will show in
                    the new currency. This won't affect pending payouts.
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* ===== Payment Methods Section ===== */}
          <section className="settings-section">
            <div className="section-header">
              <div className="section-icon cyan">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
              </div>
              <div className="section-title-group">
                <h2 className="section-title">Payment Method</h2>
                <p className="section-subtitle">
                  How you receive tips from supporters
                </p>
              </div>
            </div>

            {/* PayPal Card */}
            <div className="payment-status-card">
              <div className="payment-info">
                <div
                  className={`payment-icon${paypalConnected ? "" : " disconnected"}`}
                  style={
                    !paypalConnected
                      ? { filter: "grayscale(1) brightness(0.4)" }
                      : {}
                  }
                >
                  <svg
                    width="30"
                    height="30"
                    viewBox="-23 0 302 302"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="xMidYMid"
                  >
                    <path
                      d="M217.168 23.507C203.234 7.625 178.046.816 145.823.816h-93.52A13.393 13.393 0 0 0 39.076 12.11L.136 259.077c-.774 4.87 2.997 9.28 7.933 9.28h57.736l14.5-91.971-.45 2.88c1.033-6.501 6.593-11.296 13.177-11.296h27.436c53.898 0 96.101-21.892 108.429-85.221.366-1.873.683-3.696.957-5.477-1.556-.824-1.556-.824 0 0 3.671-23.407-.025-39.34-12.686-53.765"
                      fill="#27346A"
                    />
                    <path
                      d="M102.397 68.84a11.737 11.737 0 0 1 5.053-1.14h73.318c8.682 0 16.78.565 24.18 1.756a101.6 101.6 0 0 1 6.177 1.182 89.928 89.928 0 0 1 8.59 2.347c3.638 1.215 7.026 2.63 10.14 4.287 3.67-23.416-.026-39.34-12.687-53.765C203.226 7.625 178.046.816 145.823.816H52.295C45.71.816 40.108 5.61 39.076 12.11L.136 259.068c-.774 4.878 2.997 9.282 7.925 9.282h57.744L95.888 77.58a11.717 11.717 0 0 1 6.509-8.74z"
                      fill="#27346A"
                    />
                    <path
                      d="M228.897 82.749c-12.328 63.32-54.53 85.221-108.429 85.221H93.024c-6.584 0-12.145 4.795-13.168 11.296L61.817 293.621c-.674 4.262 2.622 8.124 6.934 8.124h48.67a11.71 11.71 0 0 0 11.563-9.88l.474-2.48 9.173-58.136.591-3.213a11.71 11.71 0 0 1 11.562-9.88h7.284c47.147 0 84.064-19.154 94.852-74.55 4.503-23.15 2.173-42.478-9.739-56.054-3.613-4.112-8.1-7.508-13.327-10.28-.283 1.79-.59 3.604-.957 5.477z"
                      fill="#2790C3"
                    />
                    <path
                      d="M216.952 72.128a89.928 89.928 0 0 0-5.818-1.49 109.904 109.904 0 0 0-6.177-1.174c-7.408-1.199-15.5-1.765-24.19-1.765h-73.309a11.57 11.57 0 0 0-5.053 1.149 11.683 11.683 0 0 0-6.51 8.74l-15.582 98.798-.45 2.88c1.025-6.501 6.585-11.296 13.17-11.296h27.444c53.898 0 96.1-21.892 108.428-85.221.367-1.873.675-3.688.958-5.477-3.122-1.648-6.501-3.072-10.14-4.279a83.26 83.26 0 0 0-2.77-.865"
                      fill="#1F264F"
                    />
                  </svg>
                </div>
                <div className="payment-details">
                  <h4>PayPal</h4>
                  <p>
                    {paypalConnected
                      ? userlogin.paypalConnectedEmail || "sha***@example.com"
                      : "Not connected"}
                  </p>
                </div>
              </div>
              <div className="payment-actions">
                {paypalConnected ? (
                  <>
                    <span className="status-badge connected">
                      <svg
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Connected
                    </span>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={openPayPalDeleteModal}
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <>
                    <span className="status-badge disconnected">
                      <svg
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      Not Connected
                    </span>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={connectPaypal}
                      disabled={isConnectingPaypal}
                    >
                      {isConnectingPaypal ? "Connecting..." : "Connect"}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Ryft Card */}
            {["arbaz", "Velo_City", "VeloCityExtra", "izaya"].includes(
              userlogin.sponsaUserName,
            ) && (
              <div className="payment-status-card mt-4">
                <div className="payment-info">
                  <div
                    className={`payment-icon${ryftConnected ? "" : " disconnected"}`}
                    style={
                      !ryftConnected
                        ? { filter: "grayscale(1) brightness(0.4)" }
                        : {}
                    }
                  >
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="40" height="40" rx="8" fill="none" />
                      <text
                        x="20"
                        y="27"
                        textAnchor="middle"
                        fontFamily="DM Sans, sans-serif"
                        fontSize="20"
                        fontWeight="700"
                        fill="#00BFA6"
                      >
                        R
                      </text>
                    </svg>
                  </div>
                  <div className="payment-details">
                    <h4>
                      Ryft
                      <span className="badge-new">New</span>
                    </h4>
                    <p>
                      {ryftConnected ? ryftConnectedEmail : "Not connected"}
                    </p>
                  </div>
                </div>
                <div className="payment-actions">
                  {ryftConnected ? (
                    <>
                      <span className="status-badge connected">
                        <svg
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Connected
                      </span>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowDisconnectRyft(true)}
                      >
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="status-badge disconnected">
                        <svg
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                        Not Connected
                      </span>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleConnectRyft}
                        disabled={isConnectingRyft} // ← add this
                      >
                        {isConnectingRyft ? "Connecting..." : "Connect"}{" "}
                        {/* ← and this */}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </section>

          <div style={{ height: 80 }} />
        </div>
      </div>

      {/* ===== Save Bar - Only appears for currency changes ===== */}
      <SaveBar
        visible={dirty}
        onDiscard={handleDiscardClick}
        onSave={handleSave}
      />

      {/* ===== Modals ===== */}
      <ConfirmModal
        open={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        onConfirm={handleDiscardConfirm}
        title="Discard changes?"
        description="Any unsaved changes will be lost. This action cannot be undone."
        confirmText="Discard"
        cancelText="Keep Editing"
        isDanger={false}
      />

      <ConfirmModal
        open={showDisconnectPaypal}
        onClose={() => setShowDisconnectPaypal(false)}
        onConfirm={handleDisconnectPaypal}
        title="Disconnect PayPal?"
        description="You will stop receiving tips via PayPal. You can reconnect anytime."
        confirmText="Disconnect"
        cancelText="Cancel"
        isDanger={true}
      />

      <ConfirmModal
        open={showDisconnectRyft}
        onClose={() => setShowDisconnectRyft(false)}
        onConfirm={handleDisconnectRyft}
        title="Disconnect Ryft?"
        description="You will stop receiving tips via Ryft. You can reconnect anytime."
        confirmText="Disconnect"
        cancelText="Cancel"
        isDanger={true}
      />

      <OnboardingCompleteModal
        open={showSuccessModal}
        onClose={handleCloseSuccessModal}
        onModalClose={handleCloseSuccessModal}
      />
    </>
  );
}
